import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';

import { ACTION_CREATOR_NAME, errorToBasketErrorModel } from 'helpers';
import { BasketErrorModel } from 'models/Errors';
import { CheckoutActionTypes, CheckoutActions } from 'store/checkout';
import { ErrorAction, AppState } from 'store';
import { initCheckoutPageSaga } from '../initCheckoutPage';
import { validateBasketReservationsSaga } from '../../../basket/actions';
import { BasketActionTypes } from '../../../basket';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { loadDeliveryDetailsSaga } from '../loadDeliveryDetails';
import { initIfcApplicationDataSaga } from './initIfcApplicationData';

const RESERVATION_ERROR_MESSAGE = 'Reservation has expired (init the first page of finance application)';

export interface InitFirstFinanceApplicationPageAction extends Action<CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE> {
}

export interface InitFirstFinanceApplicationPageActionStart extends Action<CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_START> {
}

export interface InitFirstFinanceApplicationPageActionSuccess extends Action<CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_SUCCESS> {
}

export interface InitFirstFinanceApplicationPageActionError extends ErrorAction<CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_ERROR> {
}

export const initFirstFinanceApplicationPage: () => InitFirstFinanceApplicationPageAction = () => ({
    type: CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE
});

initFirstFinanceApplicationPage[ACTION_CREATOR_NAME] = 'initFirstFinanceApplicationPage';

export type InitFirstFinanceApplicationPageFunc = (...args: Parameters<typeof initFirstFinanceApplicationPage>) => void;

export function* initFirstFinanceApplicationPageSaga(): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_START
    });

    try {
        let state: AppState = yield select();

        // ReturnToJourney - start on any finance page - there is nothing initialized in redux store
        if (!state.basketState.basket) {
            yield callBasketSaga(initCheckoutPageSaga);
        }

        if (state.basketState.shouldShowReservationWarning) {
            throw new BasketErrorModel(RESERVATION_ERROR_MESSAGE, undefined, 'initFirstFinanceApplicationPageSaga');
        }

        // ReturnToJourney - start on any finance page
        // or
        // ReturnToJourney - start on checkout page between DeliveryDetails and Finance form
        // basket is initialized (part of initCheckoutPageSaga), but delivery details is not loaded
        if (!state.checkoutState.deliveryDetails) {
            yield callBasketSaga(loadDeliveryDetailsSaga);

            state = yield select();
            yield callBasketSaga(initIfcApplicationDataSaga, {
                type: CheckoutActionTypes.INIT_IFC_APPLICATION_DATA,
                customerDeliveryData: state.checkoutState.deliveryDetails,
                questionSet: state.checkoutState.ifcQuestionSet!,
            });
        }

        // otherwise
        // flow was started on DeliveryDetails page
        // everything was initialized on that page, we don't need to do anything

        yield callBasketSaga(validateBasketReservationsSaga, {
            type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS,
            isBasketPage: false
        });

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_ERROR,
            error: errorToBasketErrorModel(error, 'initFirstFinanceApplicationPageSaga')
        });

        return false;
    }
}